import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoCallService } from '../services/videoCallService';
import type { CallHistoryItem } from '../services/videoCallService';
import { Phone, Video, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, X } from 'lucide-react';

interface CallHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCallUser?: (userId: number) => void;
}

export const CallHistoryModal: React.FC<CallHistoryModalProps> = ({
  isOpen,
  onClose,
  onCallUser
}) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<CallHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'missed'>('all');

  // Load call history
  React.useEffect(() => {
    if (isOpen) {
      loadCallHistory();
    }
  }, [isOpen]);

  const loadCallHistory = async () => {
    setIsLoading(true);
    try {
      const data = await videoCallService.getCallHistory(0, 50);
      setHistory(data);
    } catch (error) {
      console.error('Failed to load call history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString('vi-VN');
  };

  // Filter history
  const filteredHistory = activeTab === 'all'
    ? history
    : history.filter(item => item.status === 'MISSED');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Lịch sử cuộc gọi</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 py-3 border-b border-gray-200 flex gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tất cả ({history.length})
          </button>
          <button
            onClick={() => setActiveTab('missed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'missed'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Nhớ ({history.filter(h => h.status === 'MISSED').length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Chưa có cuộc gọi nào
              </h3>
              <p className="text-gray-500">
                {activeTab === 'missed'
                  ? 'Bạn chưa có cuộc gọi nhỡ nào'
                  : 'Lịch sử cuộc gọi sẽ hiển thị ở đây'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredHistory.map((call) => (
                <CallHistoryItemComponent
                  key={call.id}
                  call={call}
                  onCall={() => {
                    if (onCallUser) {
                      onCallUser(call.caller.id);
                    }
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Call history item component
interface CallHistoryItemComponentProps {
  call: CallHistoryItem;
  onCall: () => void;
}

const CallHistoryItemComponent: React.FC<CallHistoryItemComponentProps> = ({ call, onCall }) => {
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString('vi-VN');
  };

  const getStatusIcon = () => {
    switch (call.status) {
      case 'MISSED':
        return <PhoneMissed className="w-5 h-5 text-red-600" />;
      case 'REJECTED':
        return <PhoneMissed className="w-5 h-5 text-orange-600" />;
      case 'ENDED':
        return call.duration && call.duration > 0 ? (
          <PhoneIncoming className="w-5 h-5 text-green-600" />
        ) : (
          <PhoneOutgoing className="w-5 h-5 text-blue-600" />
        );
      default:
        return <Phone className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusText = () => {
    switch (call.status) {
      case 'MISSED':
        return 'Nhỡ';
      case 'REJECTED':
        return 'Từ chối';
      case 'ENDED':
        return formatDuration(call.duration);
      case 'FAILED':
        return 'Thất bại';
      default:
        return call.status;
    }
  };

  const statusColor = call.status === 'MISSED' ? 'text-red-600' : 'text-gray-600';

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group">
      {/* Caller Avatar */}
      <img
        src={call.caller.avatarUrl || '/default-avatar.png'}
        alt={call.caller.displayName}
        className="w-12 h-12 rounded-full ring-2 ring-gray-200"
      />

      {/* Call Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 truncate">
            {call.caller.displayName}
          </h3>
          {call.participantCount > 2 && (
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
              +{call.participantCount - 1}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {getStatusIcon()}
          <span className={statusColor}>
            {getStatusText()}
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-500">{formatDate(call.startedAt)}</span>
        </div>
      </div>

      {/* Call Type Badge */}
      <div className="flex items-center gap-2">
        {call.type === 'VIDEO' ? (
          <div className="p-2 bg-blue-100 rounded-lg">
            <Video className="w-4 h-4 text-blue-600" />
          </div>
        ) : (
          <div className="p-2 bg-green-100 rounded-lg">
            <Phone className="w-4 h-4 text-green-600" />
          </div>
        )}

        {/* Quick Call Button */}
        <button
          onClick={onCall}
          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all"
        >
          {call.type === 'VIDEO' ? (
            <Video className="w-4 h-4" />
          ) : (
            <Phone className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};
